/**
 * How often the "support this crag" ask is allowed on screen: once a day, per
 * crag.
 *
 * A good day at a cliff is ten sends, and an ask that arrives on every one of
 * them is an ask that gets dismissed without being read — and reads as the app
 * begging. Once a day is enough to be seen and rare enough to still mean
 * something.
 *
 * Per crag rather than per day outright: a climber who drives between two
 * cliffs in a day is being asked about two different clubs, and the second one
 * should not lose its turn because the first one had it.
 *
 * Kept in localStorage, so it is per device — the alternative is a table and a
 * write on every send, which is a lot of machinery for a nudge. The worst a
 * lost record does is show the dialog once more on another phone.
 *
 * All access is defensive: a disabled or full store degrades to "ask", never to
 * a thrown error in the middle of saving a send.
 */

import { format } from 'date-fns';

const STORAGE_PREFIX = 'climb_log.crag_support_prompt.v1:';

function keyFor(cragSlug: string): string {
  return `${STORAGE_PREFIX}${cragSlug}`;
}

function today(now: Date): string {
  return format(now, 'yyyy-MM-dd');
}

/** Whether this crag's maintainers may be put in front of the climber today. */
export function shouldPromptForCragSupport(
  cragSlug: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (typeof window === 'undefined' || !cragSlug) return false;
  try {
    return window.localStorage.getItem(keyFor(cragSlug)) !== today(now);
  } catch {
    // No store to remember with: better one ask too many than none at all.
    return true;
  }
}

/** Record that the ask has been made for this crag today. */
export function markCragSupportPrompted(
  cragSlug: string | null | undefined,
  now: Date = new Date()
): void {
  if (typeof window === 'undefined' || !cragSlug) return;
  try {
    window.localStorage.setItem(keyFor(cragSlug), today(now));
  } catch {
    /* non-fatal: storage unavailable/full */
  }
}
